import { checkProcessEnv } from '../../../helper/process-env-universal'

export function wrapCallGlobalLifecycle(globalConfig, isExportMode, closure) {
  return function callGlobalLifecycle(ctx, options) {
    checkProcessEnv()
    if (!ctx && !options.isModule) {
      throw new Error('除 module 时序外，均需提供有效 ctx')
    }

    const { lifecycleStatus, resolveIsFirstPage } = closure

    const isFirstPage = resolveIsFirstPage()
    // 计算当前状态，lifecycleStatus 总是表示当前时刻的运行时状态
    // 非当前状态使用常量获取
    // - process.dev(仅webpack范围) 是否在本地开发环境，通常通过 localhost 访问在本地调试
    // - process.server(仅webpack范围) 是否在 server 运行（含 export 的构建阶段）
    // - process.browser(仅webpack范围) 是否在 browser 运行
    // - process.env.BUILD_ENV 构建环境 local|test|prod
    Object.assign(
      lifecycleStatus,
      {
        // 当前，是否处于 module 运行阶段。module 阶段不存在 ctx
        isModule: false,
        // 当前，是否处于 App 运行阶段
        isApp: false,
        // 当前，是否处于 Page 运行阶段
        isPage: false,
        // 当前，是否为 App/Page 实例运行阶段，为 false 为 getInitialProps 阶段
        isInstance: false,
      },
      options,
      {
        // 当前运行模式是否是 export，包括 server / client
        isExportMode,
        // 当前，是否运行为普通 ssr，不含 export
        isSsr: process.server && !isExportMode && isFirstPage,
        // 当前，是否在 export 模式下运行，不含普通 ssr
        isExport: process.server && isExportMode && isFirstPage,
        // 当前，是否为 csr
        isCsr: process.browser,
        // 当前，是否为首个页面
        isFirstPage,
      }
    )

    // 状态检查。从状态语义上，尽可能早的发现错误
    if (process.server && !isFirstPage) {
      // server 端运行，不存在容器路由切换，ssr/export 均是加载页面入口渲染，故总是为首个页面
      throw new Error('逻辑错误 ' + JSON.stringify(lifecycleStatus))
    }
    if (lifecycleStatus.isModule && lifecycleStatus.isInstance) {
      // module阶段，不可能为实例
      throw new Error('逻辑错误 ' + JSON.stringify(lifecycleStatus))
    }
    if (
      [
        lifecycleStatus.isModule,
        lifecycleStatus.isApp,
        lifecycleStatus.isPage,
      ].filter(Boolean).length !== 1
    ) {
      // 代码运行时，必然三者其一
      throw new Error('逻辑错误 ' + JSON.stringify(lifecycleStatus))
    }
    if (
      [
        lifecycleStatus.isSsr,
        lifecycleStatus.isExport,
        lifecycleStatus.isCsr,
      ].filter(Boolean).length !== 1
    ) {
      // 代码运行时刻的工作状态，三者正交且状态完整
      // 即，任意时刻，isSsr/isExport/isCsr 三者当前仅当有一项为true
      throw new Error('逻辑错误 ' + JSON.stringify(lifecycleStatus))
    }

    // 生命周期处理方法，对多种不同场景收拢，以便业务站点定制行为
    //  - server
    //    - module 初始化
    //    - 静态执行 App/Page getInitialProps
    //    - 动态执行 App/Page constructor
    //  - browser
    //    - module 初始化
    //    - * 首个页面不会静态执行。原因为 next 设计首个静态页状态全部由 server 提供
    //    - 首个页面 动态执行 App/Page constructor
    //    - 其他页面 静态执行 App/Page getInitialProps
    //    - 其他页面 动态执行 App/Page constructor
    //  - 状态与同步异步
    //    - module 阶段无 ctx，其余阶段都有
    //    - module / getInitialProps 阶段支持异步任务
    //    - constructor 仅支持同步任务
    //    - 不要在 browser 的 getInitialProps 阶段执行长异步任务，会变为关键路径阻塞渲染
    // 判断方法：
    //    - process.browser 是否 browser 端运行，否则为 server
    //    - isModule 是否是 App/Page 操作前的模块处理阶段，此时无 ctx，支持异步
    //    - isStatic 是否 server/browser 的静态时序（getInitialProps）,否则为 constructor
    //    - isFirstPage 是否是首次渲染页面，对于 ssr 页面，状态已在 server 完成推导，应做行为区分
    // 执行后的结果分两类，补充 ctx 直接注入原 ctx，返回的 pageProps 注入页面
    // 注意，在不同时序上多次调用，留意重复实例化的问题
    // 参考时序
    // - server
    // {"isModule":true,"isApp":false,"isPage":false,"isInstance":false,"isSsr":true,"isExport":false,"isCsr":false,"isFirstPage":true,"ctx":false}
    // {"isModule":false,"isApp":true,"isPage":false,"isInstance":false,"isSsr":true,"isExport":false,"isCsr":false,"isFirstPage":true,"ctx":true}
    // {"isModule":false,"isApp":false,"isPage":true,"isInstance":false,"isSsr":true,"isExport":false,"isCsr":false,"isFirstPage":true,"ctx":true}
    // {"isModule":false,"isApp":true,"isPage":false,"isInstance":true,"isSsr":true,"isExport":false,"isCsr":false,"isFirstPage":true,"ctx":true}
    // {"isModule":false,"isApp":false,"isPage":true,"isInstance":true,"isSsr":true,"isExport":false,"isCsr":false,"isFirstPage":true,"ctx":true}
    // - browser 首页
    // {"isModule":true,"isApp":false,"isPage":false,"isInstance":false,"isSsr":false,"isExport":false,"isCsr":true,"isFirstPage":true,"ctx":false}
    // * App / Page 均不触发 static/getInitialProps 时序，其状态已在 server 推导完成
    // {"isModule":false,"isApp":true,"isPage":false,"isInstance":true,"isSsr":false,"isExport":false,"isCsr":true,"isFirstPage":true,"ctx":true}
    // {"isModule":false,"isApp":false,"isPage":true,"isInstance":true,"isSsr":false,"isExport":false,"isCsr":true,"isFirstPage":true,"ctx":true}
    // - browser 路由切换
    // {"isModule":false,"isApp":true,"isPage":false,"isInstance":false,"isSsr":false,"isExport":false,"isCsr":true,"isFirstPage":false,"ctx":true}
    // {"isModule":false,"isApp":false,"isPage":true,"isInstance":false,"isSsr":false,"isExport":false,"isCsr":true,"isFirstPage":false,"ctx":true}
    // {"isModule":false,"isApp":true,"isPage":false,"isInstance":true,"isSsr":false,"isExport":false,"isCsr":true,"isFirstPage":true,"ctx":true}
    // {"isModule":false,"isApp":false,"isPage":true,"isInstance":true,"isSsr":false,"isExport":false,"isCsr":true,"isFirstPage":false,"ctx":true}
    if (!globalConfig.lifecycle) {
      return {}
    }

    const callResult = []
    globalConfig.lifecycle(ctx, callResult, lifecycleStatus)
    if (lifecycleStatus.isInstance || lifecycleStatus.isModule) {
      // 不支持异步
      const returnResult = {}
      callResult.forEach((r) => {
        if (r) {
          if (typeof r.then === 'function') {
            throw new Error('instance 时序不能返回异步结果')
          }
          if (typeof r === 'object') {
            Object.assign(returnResult, r)
          } else {
            if (process.dev) {
              console.warn(
                'globalConfig.lifecycle(ctx, callResult, lifecycleStatus) 中，callResult 的项必须为Object'
              )
              console.log(r)
            }
          }
        }
      })
      return returnResult
    } else {
      // 支持异步
      // 支持异步的场景，外部均要求正确 await，实际使用 lifecycle 时，可视为其总是 { ... }，而非 promise
      return Promise.all(callResult).then((d) => {
        const returnResult = d.reduce((o, r) => {
          if (typeof r === 'object') {
            Object.assign(o, r)
          } else {
            if (process.dev) {
              console.warn(
                'globalConfig.lifecycle(ctx, callResult, lifecycleStatus) 中，callResult 的项必须为Promise<Object>或Object'
              )
              console.log(r)
            }
          }
          return o
        }, {})
        return returnResult
      })
    }
  }
}
